import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';

import { UserHosting200RowsItem } from '@newfold/huapi-js/src/index.schemas';

import LoadingScene from '~/components/MFEProvider/lib/ReactSuspense/LoadingScene';
import Tabs from '~/components/Tabs';
import SearchBar from '~/components/VisionUI/SearchBar';
import useQueryFeature from '~/hooks/useQueryFeature';
import MultiStepCardHeader from '~/scenes/AddSetupSiteFlows/components/MultiStepCard/CardHeader';
import { convertHyphensToDots } from '~/utils/hosting/helpers';

import SelfManagedBreadcrumbs from '../../components/SelfManagedBreadcrumbs';
import { useReimageStatus } from '../../hooks/useReimageStatus';

interface SelfManagedHostingLayoutProps {
  isLoadingAccounts?: boolean;
  hostingPlanDetails?: UserHosting200RowsItem | null;
  searchQuery?: string;
  onSearchChange?: (
    value: string,
    currentReimageTab?: 'plain-os' | 'applications' | null,
  ) => void;
  currentReimageTab?: 'plain-os' | 'applications' | null;
}

const REIMAGE_TAB_ROUTES = new Set(['plain-os', 'applications']);

const SelfManagedHostingLayout = ({
  isLoadingAccounts = false,
  hostingPlanDetails = null,
  searchQuery = '',
  onSearchChange = () => undefined,
  currentReimageTab = null,
}: SelfManagedHostingLayoutProps) => {
  const { t } = useTranslation('selfManagedHosting');
  const navigate = useNavigate();
  const { hostingId: hostingIdWithHyphens } = useParams();
  const { pathname } = useLocation();
  const { isReimaging } = useReimageStatus(
    convertHyphensToDots(hostingIdWithHyphens!),
  );
  const [isSnapshotsEnabled] = useQueryFeature('enable_snapshots');

  const { prod_name: hostingPlanName = '' } = hostingPlanDetails?.billing || {};

  const currentRouteSegment = pathname.split('/').findLast(Boolean) || '';
  const isReImageServerRoute = REIMAGE_TAB_ROUTES.has(currentRouteSegment);
  const selfManagedHostingBasePath = `/${hostingIdWithHyphens}/self-managed-hosting`;
  const getTabPath = (path: string) => `${selfManagedHostingBasePath}/${path}`;

  const tabConfig = isReImageServerRoute
    ? [
        {
          title: t('tabs.plainOS'),
          path: getTabPath('reimage-server/plain-os'),
        },
        {
          title: t('tabs.applications'),
          path: getTabPath('reimage-server/applications'),
        },
      ]
    : [
        {
          title: t('tabs.overview'),
          path: getTabPath('overview'),
          dataTestId: 'tab-overview',
        },
        {
          title: t('tabs.license'),
          path: getTabPath('license'),
          disabled: isReimaging,
          dataTestId: 'tab-license',
        },
        {
          title: t('tabs.access'),
          path: getTabPath('access'),
          dataTestId: 'tab-access',
        },

        ...(isSnapshotsEnabled
          ? [
              {
                title: t('snapshots.title'),
                path: getTabPath('snapshots'),
              },
            ]
          : []),
        {
          title: t('tabs.sshKeys'),
          path: getTabPath('ssh-keys'),
          dataTestId: 'tab-ssh-keys',
        },
        {
          title: t('tabs.aiTooling'),
          path: getTabPath('ai-tooling'),
          dataTestId: 'tab-ai-tooling',
        },
      ];

  const headerTitle = isReImageServerRoute
    ? String(t('reImageSection'))
    : hostingPlanName || String(t('hostingPlan'));

  if (isLoadingAccounts) return <LoadingScene />;

  const handleSearch = (value: string) => {
    onSearchChange(value, currentReimageTab);
  };

  const handleBack = () => {
    navigate(getTabPath('overview'));
  };

  return (
    <Stack spacing={4}>
      <SelfManagedBreadcrumbs />
      <MultiStepCardHeader
        headerTitle={headerTitle}
        headerVariant="h1"
        hasBackArrow={isReImageServerRoute}
        handleBack={handleBack}
      />
      {isReImageServerRoute ? (
        <Grid container spacing={2} alignItems="center" sx={{ width: '100%' }}>
          <Grid item xs={12} md>
            <Tabs pages={tabConfig} />
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
            }}
          >
            <SearchBar
              searchQuery={searchQuery}
              placeholder={t('reimageSearch.placeholder')}
              handleSearch={handleSearch}
              cancelSearch={() => onSearchChange('')}
              inputProps={{ sx: { backgroundColor: 'common.white' } }}
            />
          </Grid>
        </Grid>
      ) : (
        <Tabs pages={tabConfig} />
      )}
      <Outlet />
    </Stack>
  );
};

export default SelfManagedHostingLayout;
